// CallbackService.js
// Centralized callback management service for the Ottu JS SDK

class CallbackService {
  constructor(curScriptElement, fieldsService) {
    this.curScriptElement = curScriptElement;
    this.fieldsService = fieldsService;
  }

  /**
   * Check if a callback is configured
   * @param {string} dataAttribute - The data attribute to check (e.g., 'data-success')
   * @param {string} fallbackName - Fallback callback name
   * @returns {boolean} - Returns true if callback is configured
   */
  checkCallback(dataAttribute) {
    return (
      this.curScriptElement && this.curScriptElement.getAttribute(dataAttribute)
    );
  }

  /**
   * Execute success callback
   * @param {Object} response - Success response object
   */
  callBackSuccess(response) {
    if (!response.redirect_url) {
      response.redirect_url = window.location.href;
    }
    if (this.checkCallback('data-success')) {
      const callbackName = this.curScriptElement.dataset.success;
      if (window[callbackName]) {
        try {
          window[callbackName](response);
        } catch (error) {
          console.log('Checkout SDK successCallback Error:', error);
        }
      }
    } else {
      console.warn('WARNING: Success callback is not configured');
    }
  }

  /**
   * Execute payment callback (before payment hook)
   * @param {Object} response - Payment response object
   * @returns {Promise} - Promise that resolves when callback is complete
   */
  callBackPayment(response) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const callbackName = this.curScriptElement.dataset.beforepayment;
      if (this.checkCallback('data-beforepayment') && window[callbackName]) {
        try {
          await window[callbackName](response || {});
          if (response && response.redirect_url) {
            window.location.href = response.redirect_url;
          }
          resolve();
        } catch (error) {
          this.fieldsService.disableEnableFields(false);
          console.log('Checkout SDK beforePayment Error:', error);
          reject(error);
        }
      } else {
        console.warn('WARNING: Before Payment Hook is not configured');
        if (response && response.redirect_url) {
          window.location.href = response.redirect_url;
        }
        resolve();
      }
    });
  }

  /**
   * Execute validation callback
   * @returns {Promise<boolean>} - Promise that resolves to validation result
   */
  callBackValidate() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const callbackName = this.curScriptElement.dataset.validatepayment;
      if (this.checkCallback('data-validatepayment') && window[callbackName]) {
        try {
          await window[callbackName]();
          resolve(true);
        } catch (error) {
          console.log('Checkout SDK validatePayment Error:', error);
          resolve(false);
        }
      } else {
        console.warn('WARNING: validatePayment Hook is not configured');
        resolve(true);
      }
    });
  }

  /**
   * Execute cancel callback
   * @param {Object} response - Cancel response object
   */
  callBackCancel(response) {
    const callbackName = this.curScriptElement.dataset.cancel;
    if (this.checkCallback('data-cancel') && window[callbackName]) {
      try {
        window[callbackName](response);
      } catch (error) {
        console.log('Checkout SDK cancelCallback Error:', error);
      }
    } else {
      console.warn('WARNING: Cancel callback is not configured');
    }
  }

  /**
   * Execute error callback
   * @param {Object} response - Error response object
   */
  callBackError(response) {
    const callbackName = this.curScriptElement.dataset.error;
    if (this.checkCallback('data-error')) {
      if (window[callbackName]) {
        try {
          window[callbackName](response);
        } catch (error) {
          console.log('Checkout SDK errorCallback Error:', error);
        }
      }
    } else {
      console.warn('WARNING: Error callback is not configured');
    }
  }

  /**
   * Get WebSocket callbacks for integration classes
   * @returns {Object} - Object containing WebSocket callback functions
   */
  getWebSocketCallbacks() {
    return {
      onSuccess: (res) => {
        this.callBackSuccess(res);
      },
      onCancel: (res) => {
        this.callBackCancel(res);
      },
      onError: (res) => {
        this.callBackError(res);
      },
      onClose: () => {
        console.log('WebSocket Connection Closed');
      },
    };
  }

  /**
   * Get all available callbacks
   * @returns {Object} - Object containing all callback functions
   */
  getAllCallbacks() {
    return {
      success: this.callBackSuccess.bind(this),
      payment: this.callBackPayment.bind(this),
      validate: this.callBackValidate.bind(this),
      cancel: this.callBackCancel.bind(this),
      error: this.callBackError.bind(this),
      webSocket: this.getWebSocketCallbacks(),
    };
  }
}

module.exports = CallbackService;
